import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Arrow, CalendarIcon } from "../components/Icons";
import { API_URL, STORAGE_URL } from "../config";
import { formatEventDate } from "../utils/TextUtils";

// The frame every state of this page shares: the header, the footer and the way back.
function Frame({ children }) {
    return (
        <div className="cf-public">
            <Header />
            <main className="cf-sec cf-pp">
                <div className="cf-wrap">
                    <nav className="cf-crumbs" aria-label="You are here">
                        <Link to="/events">Events</Link>
                        <span aria-hidden="true">/</span>
                        <span>Event details</span>
                    </nav>
                    {children}
                </div>
            </main>
            <Footer />
        </div>
    );
}

function EventDetails() {
    const { id, slug } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Fetch event data from API
    const fetchEvent = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/events/public/${id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.ok) {
                const data = await response.json();
                setEvent(data.event);
            } else {
                throw new Error("Failed to fetch event details");
            }
        } catch (error) {
            setError("An error occurred while fetching event details.");
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    }, [id, slug]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        fetchEvent();
    }, [fetchEvent]);

    if (loading) {
        return <Frame><p className="cf-empty">Loading event details...</p></Frame>;
    }

    if (error) {
        return <Frame><p className="cf-empty is-bad" role="alert">{error}</p></Frame>;
    }

    if (!event) {
        return <Frame><p className="cf-empty">No event details available.</p></Frame>;
    }

    const when = formatEventDate(event.start_date) || event.start_date;

    return (
        <Frame>
            <div className="cf-event-page">
                <div className="cf-event-page-photo">
                    {event.image
                        ? <img src={STORAGE_URL + "/events/" + event.image} alt={event.name} />
                        : <span className="cf-event-nophoto"><CalendarIcon size={48} /></span>}
                </div>

                <div>
                    {event.status && <span className="cf-badge is-info">{event.status}</span>}
                    <h1>{event.name}</h1>

                    <p className="cf-pp-lede">{event.description}</p>

                    <dl className="cf-detail-list cf-event-facts">
                        <dt>Event date</dt>
                        <dd>{when}{event.start_time ? ` at ${event.start_time}` : ""}</dd>

                        <dt>Venue</dt>
                        <dd>{event.venue}</dd>

                        {event.require_registration && (
                            <>
                                <dt>Registration fee</dt>
                                <dd>{event.registration_fee}</dd>

                                <dt>Payment option</dt>
                                <dd>{event.payment_option}</dd>

                                <dt>Payment description</dt>
                                <dd>{event.payment_description}</dd>
                            </>
                        )}
                    </dl>

                    <div className="cf-cta-row">
                        {event.require_registration && (
                            <Link className="cf-btn cf-btn-primary" to={`/dashboard/events/${event.id}/details`}>
                                Sign in to register <Arrow />
                            </Link>
                        )}
                        <Link className="cf-btn cf-btn-ghost" to="/events">All events</Link>
                    </div>
                </div>
            </div>
        </Frame>
    );
}

export default EventDetails;
